import { useQuery } from '@tanstack/react-query';
import useCheckoutState from '../hooks/useCheckoutState';
import getAuthToken from '../lib/getAuthToken';
import { checkoutKey, paymentKey, subscriptionKey } from '../queryKeys';
import api from '../api';
import getSessionId from '../lib/getSessionId';
import getUpsellFunnelStep from '../lib/getUpsellFunnelStep';

export function useInitialCheckout() {
  const { checkout, isUpsell, upsell, isTesting } = useCheckoutState();

  const token = isTesting ? getAuthToken() : null;

  const checkoutSellerId = checkout.seller._id;
  const initialCheckoutId = upsell?.initialCheckoutId;

  return useQuery({
    queryKey: checkoutKey(checkoutSellerId, initialCheckoutId, undefined, !!token),
    queryFn: () =>
      api.getCheckout(
        checkoutSellerId,
        initialCheckoutId,
        {
          isTesting,
        },
        token,
      ),
    enabled: !!isUpsell && !!initialCheckoutId && !!checkoutSellerId,
  });
}

export function useInitialPayment() {
  const { upsell, isUpsell } = useCheckoutState();

  // The cookie records the kind of purchase from the price actually bought.
  // product.type can't be used: event pages have no product, and on a
  // multi-price page it comes from the first price, not the one purchased.
  const initialSubscriptionId = upsell?.initialSubscriptionId;
  const initialPurchaseId = initialSubscriptionId || upsell?.initialPaymentId;

  const sessionId = getSessionId();

  return useQuery({
    queryKey: initialSubscriptionId
      ? subscriptionKey(initialPurchaseId, sessionId)
      : paymentKey(initialPurchaseId, sessionId),
    queryFn: () =>
      initialSubscriptionId
        ? api.getSubscription(initialPurchaseId, sessionId)
        : api.getCharge(initialPurchaseId, sessionId),
    enabled: !!isUpsell && !!initialPurchaseId,
  });
}

export function useUpsellCheckout() {
  const { checkout, isTesting } = useCheckoutState();

  const token = isTesting ? getAuthToken() : null;

  const upsellFunnelStep = getUpsellFunnelStep(checkout);

  const checkoutSellerId = checkout.seller._id;
  const upsellCheckoutId = upsellFunnelStep?.config?.checkout?._id;

  return useQuery({
    queryKey: checkoutKey(checkoutSellerId, upsellCheckoutId, undefined, !!token),
    queryFn: () =>
      api.getCheckout(
        checkoutSellerId,
        upsellCheckoutId,
        {
          isTesting,
        },
        token,
      ),
    enabled: !!upsellCheckoutId && !!checkoutSellerId,
  });
}
